import {
  ForwardedRef,
  forwardRef,
  KeyboardEventHandler,
  ReactElement,
  useCallback,
  useMemo,
  useState,
} from "react";
import { LIGHT_MODE_TEXT_COLOR } from "theme/components/text";
import {
  ChakraStylesConfig,
  CreatableSelect,
  OnChangeValue,
} from "chakra-react-select";

const createOption = (label: string) => ({
  label,
  value: label,
});

const components = {
  DropdownIndicator: null,
};

type CreatableTextInputProps = {
  onChange: (newValue: string) => void;
  value: string;
} & Omit<typeof CreatableSelect, "onBlur">;

const creatableSelectChakraStyles: ChakraStylesConfig<SelectOption, true> = {
  valueContainer: (base) => ({
    ...base,
    borderTopLeftRadius: "20px",
    borderBottomLeftRadius: "20px",
    color: LIGHT_MODE_TEXT_COLOR,
    height: "80px",
    overflowY: "auto",
    alignItems: "flex-start",
  }),
  control: (base, props) => ({
    ...base,
    borderColor: props.hasValue ? "grey.100" : "grey.500",
  }),
  option: (base) => ({ ...base, color: LIGHT_MODE_TEXT_COLOR }),
  indicatorsContainer: (base) => ({ ...base, alignItems: "flex-start" }),
  multiValue: (provided, state) =>
    state.data.value.length > 100
      ? {
          ...provided,
          border: "2px solid",
          borderColor: "warningRed",
        }
      : provided,
};

const CreatableTextInput = (
  { onChange, value, ...props }: CreatableTextInputProps,
  ref: ForwardedRef<any>
): ReactElement => {
  const [inputPoNumberValue, setInputPoNumberValue] = useState("");

  const handleTextInput = useCallback(() => {
    if (!inputPoNumberValue) return;

    // Use set to remove duplicates
    const nextValues = new Set(
      [
        ...value.split(","),
        ...inputPoNumberValue.split(",").map((item) => item.trim()),
      ].filter(Boolean)
    );

    onChange(Array.from(nextValues).join(","));
    setInputPoNumberValue("");
  }, [inputPoNumberValue, value, onChange]);

  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = useCallback(
    (event) => {
      if (!inputPoNumberValue) return;
      switch (event.key) {
        case "Enter":
        case "Tab":
          handleTextInput();
          event.preventDefault();
      }
    },
    [inputPoNumberValue, handleTextInput]
  );

  const selectValue = useMemo(
    () =>
      value?.length > 0
        ? value.split(",").map((item: string) => createOption(item))
        : [],
    [value]
  );

  return (
    <CreatableSelect
      useBasicStyles
      placeholder=""
      components={components}
      inputValue={inputPoNumberValue}
      isClearable
      isMulti
      menuIsOpen={false}
      ref={ref}
      chakraStyles={creatableSelectChakraStyles}
      value={selectValue}
      {...props}
      onBlur={handleTextInput}
      onInputChange={setInputPoNumberValue}
      onKeyDown={handleKeyDown}
      onChange={(newValue: OnChangeValue<SelectOption, true>) =>
        onChange(newValue.map((item) => item.label).join(","))
      }
    />
  );
};
CreatableTextInput.displayName = "CreatableTextInput";

export default forwardRef(CreatableTextInput);
